import React, { useEffect, useRef, useMemo, useState, useCallback } from 'react'
import SingleProductFilteringTable from '../SingleProductFilteringTable'
import { getProductImageUrl } from 'Utils/getProductImageUrl'
import { useDispatch, useSelector } from 'react-redux'
import {
  getPriceInventory,
  typesenseQueryAction,
} from '../../redux/actions/priceProductsAction'
import { emptyCart, priceListAddToCart } from '../../redux/actions/cartActions'
import { Modal } from 'react-bootstrap'
import GenericModal from 'Components/GenericModal'
import { t } from 'i18next'
import { handleAddProductToCart } from 'Utils/functions'
import { TYPESENSE_SEARCH } from 'Utils/constants'
import { getProductDetaillAction } from 'redux/actions/productActions'
import { useQueryClient } from '@tanstack/react-query'
import NetworkErrorAlert from '../NetworkErrorAlert'

const HITS_PER_PAGE = TYPESENSE_SEARCH.HITS_PER_PAGE

export default function InfiniteHits({
  SearchBox,
  client,
  setProductDetails,
  setIsOpen,
  setOpenWithInventario,
  ...props
}) {
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [allHits, setAllHits] = useState([])
  const [isLastPage, setIsLastPage] = useState(true)
  const [products, setProducts] = useState([])

  const sentinelRef = useRef(null)
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const cart = useSelector(state => state.cart.cart)
  const companyId = useSelector(state => state.company.CompanyId)
  const warehouseId = useSelector(state => state.company.WarehouseId)

  const [showAlertDontAddProduct, setShowAlertDontAddProduct] = useState(false)
  const [showNetworkErrorAlert, setShowNetworkErrorAlert] = useState(false)
  const [apiErrorMessage, setApiErrorMessage] = useState(null)
  const [showSearchErrorAlert, setShowSearchErrorAlert] = useState(false)
  const requestIdRef = useRef(0)

  const searchColumns = useMemo(
    () => [
      { Header: 'Código', accessor: 'codigo' },
      { Header: 'Nombre del producto', accessor: 'descripcion' },
      { Header: 'Imagen', accessor: 'imagen', hideEnabled: true },
      { Header: 'Marca', accessor: 'marca', hideEnabled: true },
      {
        Header: t('SELL_BY_PROD_T.IN_STORE_HEADER'),
        accessor: 'inStore',
        hideEnabled: true,
      },
      { Header: 'Descuento', accessor: 'descuento', hideEnabled: true },
      { Header: 'Precio', accessor: 'precio' },
      { Header: 'Acciones', accessor: 'acciones' },
    ],
    []
  )

  // ─── Typesense search ───────────────────────────────────────────────────────

  const runSearch = useCallback(
    async (q, p, append) => {
      if (!q) return
      // Guards against an older, slower request overwriting the results of
      // a newer one (e.g. the user types a new query before a previous
      // page/query request resolves).
      const requestId = ++requestIdRef.current
      try {
        const response = await dispatch(
          typesenseQueryAction({
            q,
            page: p,
            per_page: HITS_PER_PAGE,
            preset: TYPESENSE_SEARCH.PRESET,
          })
        )
        if (requestId !== requestIdRef.current) return

        const newHits = (response?.data?.hits || []).map(h => h.document)
        const found = response?.data?.found || 0
        const nbPages = Math.ceil(found / HITS_PER_PAGE)

        setIsLastPage(p >= nbPages)
        setAllHits(prev => (append ? [...prev, ...newHits] : newHits))
      } catch (err) {
        console.error('Error searching Typesense:', err)
        if (requestId === requestIdRef.current) setShowSearchErrorAlert(true)
      }
    },
    [dispatch]
  )

  const handleSearch = useCallback(
    newQuery => {
      if (newQuery === query) return
      setQuery(newQuery)
      setPage(1)
      setAllHits([])
      setProducts([])
    },
    [query]
  )

  const showMore = useCallback(() => {
    if (!isLastPage) setPage(p => p + 1)
  }, [isLastPage])

  useEffect(() => {
    runSearch(query, 1, false)
  }, [query])
  useEffect(() => {
    if (page > 1) runSearch(query, page, true)
  }, [page])
  // ─── Intersection observer for infinite scroll ──────────────────────────────

  useEffect(() => {
    if (sentinelRef.current === null) return
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !isLastPage) showMore()
      })
    })
    observer.observe(sentinelRef.current)
    return () => observer.disconnect()
  }, [isLastPage, showMore])

  // ─── Product formatting ─────────────────────────────────────────────────────

  const getInventoryById = async (ids, clientData) =>
    dispatch(getPriceInventory(ids, clientData))

  const formatProducts = async (rawHits, productsId, requestId) => {
    const inventory = await getInventoryById(productsId, client)

    const productsFormatted = await Promise.all(
      rawHits.map(async product => {
        const inventoryProduct = inventory?.find(
          inv => inv?.ProductId === product?.ProductId
        )

        const bundleData = await queryClient.fetchQuery({
          queryKey: [
            'productDetailsBundle',
            'v2',
            product?.ProductId,
            companyId,
            warehouseId,
          ],
          queryFn: async () => {
            const productDetailRes = await getProductDetaillAction({
              CompanyId: companyId,
              WarehouseId: warehouseId,
              ProductId: product?.ProductId,
              GlobalExecution: true,
            })()

            const data = productDetailRes?.data?.[0]
            if (!data) return {}

            const pimAttributes = data?.PIMAttributes
            const serviceSku = pimAttributes?.relatedServices?.[0]?.systemCode
            const installable =
              pimAttributes?.attributes?.coreDetail?.installable
            const serviceId = pimAttributes?.relatedServices?.[0]?.productId
            const relatedServices = pimAttributes?.relatedServices
            const productTypeId = data?.ProductTypeId ?? null

            return {
              serviceId,
              serviceSku,
              installable,
              relatedServices,
              productTypeId,
            }
          },
          staleTime: 1000 * 60 * 30,
          cacheTime: 1000 * 60 * 60 * 2,
        })

        const canInstall = !!(bundleData?.installable && bundleData?.serviceId)
        const installationService = canInstall
          ? { ...bundleData, productSku: product?.Sku }
          : null

        return {
          ...product,
          codigo: product?.Sku,
          descripcion: product?.ProductName,
          imagen: getProductImageUrl(product),
          marca: product?.Brand,
          inStore: inventoryProduct?.WarehouseQty || 0,
          descuento: inventoryProduct?.Discount || 0,
          precio: inventoryProduct?.FinalPrice || 0,
          acciones: {
            canAdd: !!(
              inventoryProduct?.FinalPrice && inventoryProduct?.IsActive
            ),
            canInstall,
            installationService,
          },
          Cost: inventoryProduct?.Cost || 0,
          AvgCost: inventoryProduct?.AvgCost || 0,
          AvgPrice: inventoryProduct?.AvgPrice || 0,
          BasePrice: inventoryProduct?.BasePrice || 0,
          FinalPrice: inventoryProduct?.FinalPrice ?? null,
          TaxCategoryValue:
            inventoryProduct?.TaxCategoryValue ??
            product?.TaxCategoryValue ??
            0,
          IsActive: inventoryProduct?.IsActive ?? product?.IsActive ?? false,
          ProductTypeId: bundleData?.productTypeId ?? product?.ProductTypeId,
          Quantity: 1,
        }
      })
    )

    // Enrichment (inventory + per-product installation bundle lookups) is
    // slow; if a newer search has started since this call began, its own
    // formatProducts call owns `products` now, so this stale result is
    // discarded instead of overwriting it.
    if (requestId !== requestIdRef.current) return
    setProducts(productsFormatted)
  }

  useEffect(() => {
    if (!allHits.length) return
    const ids = allHits.map(h => h?.ProductId)
    formatProducts(allHits, ids, requestIdRef.current)
  }, [allHits])
  // ─── Cart helpers ───────────────────────────────────────────────────────────

  const addToCart = product => {
    handleAddProductToCart({
      product,
      cart,
      dispatch,
      customerId: client?.CustomerId,
      setShowAlertDontAddProduct,
      setShowNetworkErrorAlert,
      setApiErrorMessage,
    })
  }

  const saveCart = newCart => {
    dispatch(priceListAddToCart(newCart, client?.CustomerId))
  }

  const checkProductInCartByClient = () => {
    if (!navigator.onLine) return
    const newCart = [...cart]
    dispatch(emptyCart())
    saveCart(newCart)
  }

  useEffect(() => {
    if (client) checkProductInCartByClient()
  }, [client])
  // ─── Inject onSearch into the SearchBox element ─────────────────────────────

  const BoundSearchBox = SearchBox
    ? React.cloneElement(SearchBox, { onSearch: handleSearch })
    : null

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div data-testid="container-infinite-hits">
      <div className="ais-InfiniteHits" data-testid="container-hits-list">
        <div className="ais-InfiniteHits-list">
          <SingleProductFilteringTable
            sentinelRef={sentinelRef}
            SearchBox={BoundSearchBox}
            data={products}
            columns={searchColumns}
            hideFooter={true}
            addToCart={addToCart}
            setProductDetails={setProductDetails}
            setIsOpen={setIsOpen}
            setOpenWithInventario={setOpenWithInventario}
          />
        </div>
      </div>

      <Modal
        show={showAlertDontAddProduct}
        backdrop="static"
        className="modal-clase-price-change"
        centered
        data-testid="modal-no-inventory-alert">
        <GenericModal
          setIsOpen={() => setShowAlertDontAddProduct(false)}
          showCaution={true}
          hideCheck={true}
          text={t('GENERIC_MODAL.NO_INVENTORY_ALERT')}
          mostrarButtons={false}
        />
      </Modal>

      <NetworkErrorAlert
        open={showNetworkErrorAlert}
        message={t('GENERIC_MODAL.NETWORK_ERROR_ADD_PRODUCT')}
        onClose={() => setShowNetworkErrorAlert(false)}
      />
      <NetworkErrorAlert
        open={apiErrorMessage !== null}
        message={
          t('GENERIC_MODAL.API_ERROR_ADD_PRODUCT') +
          (apiErrorMessage ? ': ' + apiErrorMessage : '.')
        }
        onClose={() => setApiErrorMessage(null)}
      />
      <NetworkErrorAlert
        open={showSearchErrorAlert}
        message={t('GENERIC_MODAL.SEARCH_ERROR_T')}
        onClose={() => setShowSearchErrorAlert(false)}
      />
    </div>
  )
}
